import { createReducer } from '@reduxjs/toolkit';
import {
    IClientDto,
    ClientDto,
    ClientType,
    IProjectDto,
    ProjectDto,
    ISiteDto,
    SiteDto,
    SiteType,
} from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyCustomer,
    clearSite,
    updateClientAddress,
    updateClientCity,
    updateClientClientType,
    updateClientContactInfo,
    updateClientEmail,
    updateClientEntityType,
    updateClientName,
    updateClientShortName,
    updateClientTaxId,
    updateClientPhone,
    updateClientUcin,
    selectClient,
    toggleCreateNewClient,
    createNewEmptyProject,
    selectProject,
    toggleCreateNewProject,
    updateProjectName,
    createNewEmptySite,
    updateSiteLocationInfo,
    updateSiteName,
    updateSiteSiteType,
    importClientsForSiteForm,
    importProjectsForSiteForm,
    importSitesForProjectForSiteForm,
    saveSite,
    importSiteForSiteForm,
    importClientForSiteForm,
    updateClientState,
    createNewEmptyWarehouse,
    createNewEmptyConstructionSite,
} from '../actions/siteActions';
import { SaveEntityStatus } from './common';

export interface SiteReducerState {
    clients: IClientDto[] | null;
    projects: IProjectDto[] | null;
    sites: ISiteDto[] | null;

    client: IClientDto | null;
    createNewClient: boolean;
    newClientId: number | null;

    project: IProjectDto | null;
    createNewProject: boolean;
    newProjectId: number | null;

    site: ISiteDto | null;
    oldSiteId: number;

    saveStatus: SaveEntityStatus;
}

function clearState(state: SiteReducerState): void {
    state.clients = null;
    state.projects = null;
    state.sites = null;

    state.client = null;
    state.createNewClient = false;
    state.newClientId = null;

    state.project = null;
    state.createNewProject = false;
    state.newProjectId = null;

    state.site = null;

    state.saveStatus = SaveEntityStatus.None;
}

function getInitialState(): SiteReducerState {
    const state: SiteReducerState = {
        clients: null,
        projects: null,
        sites: null,

        client: null,
        createNewClient: false,
        newClientId: null,

        project: null,
        createNewProject: false,
        newProjectId: null,

        site: null,
        oldSiteId: 0,

        saveStatus: SaveEntityStatus.None,
    };

    clearState(state);

    return state;
}

export const siteFormReducer = createReducer<SiteReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importSiteForSiteForm.fulfilled, (state: SiteReducerState, action: any) => {
                state.site = action.payload ?? new SiteDto();
                state.oldSiteId = (action.payload as SiteDto).id ?? 0;
                state.newClientId = (action.payload as SiteDto).project?.clientId ?? 0;
                state.newProjectId = (action.payload as SiteDto).projectId ?? 0;
            })
            .addCase(importClientForSiteForm.fulfilled, (state: SiteReducerState, action: any) => {
                state.client = action.payload ?? new ClientDto();
            })
            .addCase(importClientsForSiteForm.fulfilled, (state: SiteReducerState, action: any) => {
                state.clients = action.payload ?? [];
            })
            .addCase(importProjectsForSiteForm.fulfilled, (state: SiteReducerState, action: any) => {
                state.projects = action.payload ?? [];
            })
            .addCase(importSitesForProjectForSiteForm.fulfilled, (state: SiteReducerState, action: any) => {
                state.sites = action.payload ?? [];
            })
            .addCase(selectClient, (state: SiteReducerState, action: any) => {
                if (!action.payload) {
                    state.project = null;
                    state.newProjectId = null;
                }

                state.newClientId = action.payload;
            })
            .addCase(toggleCreateNewClient, (state: SiteReducerState, action: any) => {
                if (!state.createNewClient) {
                    state.createNewProject = true;
                }
                state.createNewClient = !state.createNewClient;
            })
            .addCase(createNewEmptyCustomer, (state: SiteReducerState, action: any) => {
                const newClient: IClientDto = new ClientDto();

                newClient.id = 0;
                newClient.address = undefined;
                newClient.city = undefined;
                newClient.clientType = ClientType.Customer;
                newClient.contactInfo = undefined;
                newClient.email = undefined;
                newClient.entityType = undefined;
                newClient.name = undefined;
                newClient.phone = undefined;
                newClient.shortName = undefined;
                newClient.taxId = undefined;
                newClient.ucin = undefined;

                state.client = newClient;
            })
            .addCase(clearSite, (state: SiteReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateClientAddress, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, address: action.payload };
            })
            .addCase(updateClientCity, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, city: action.payload };
            })
            .addCase(updateClientState, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, state: action.payload };
            })
            .addCase(updateClientClientType, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, clientType: action.payload };
            })
            .addCase(updateClientContactInfo, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, contactInfo: action.payload };
            })
            .addCase(updateClientEmail, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, email: action.payload };
            })
            .addCase(updateClientEntityType, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, entityType: action.payload };
            })
            .addCase(updateClientName, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, name: action.payload };
            })
            .addCase(updateClientShortName, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, shortName: action.payload };
            })
            .addCase(updateClientTaxId, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, taxId: action.payload };
            })
            .addCase(updateClientPhone, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, phone: action.payload };
            })
            .addCase(updateClientUcin, (state: SiteReducerState, action: any) => {
                myAssert(() => state.client !== null, 'There needs to be client if it is being updated');

                state.client! = { ...state.client, ucin: action.payload };
            })
            .addCase(selectProject, (state: SiteReducerState, action: any) => {
                state.newProjectId = action.payload;
            })
            .addCase(toggleCreateNewProject, (state: SiteReducerState, action: any) => {
                state.createNewProject = !state.createNewProject;
            })
            .addCase(createNewEmptyProject, (state: SiteReducerState, action: any) => {
                const newProject: IProjectDto = new ProjectDto();

                newProject.id = 0;
                newProject.name = undefined;
                newProject.clientId = 0;
                newProject.sites = undefined;

                state.project = newProject;
            })
            .addCase(updateProjectName, (state: SiteReducerState, action: any) => {
                myAssert(() => state.project !== null, 'There needs to be project if it is being updated');

                state.project! = { ...state.project, name: action.payload };
            })
            .addCase(createNewEmptySite, (state: SiteReducerState, action: any) => {
                const newSite: ISiteDto = new SiteDto();

                newSite.id = 0;
                newSite.name = undefined;
                newSite.goods = undefined;
                newSite.locationInfo = undefined;
                newSite.projectId = 0;
                newSite.project = undefined;
                newSite.siteType = undefined;
                newSite.data = undefined;

                state.site = newSite;
            })
            .addCase(createNewEmptyWarehouse, (state: SiteReducerState, action: any) => {
                const newSite: ISiteDto = new SiteDto();

                newSite.id = 0;
                newSite.name = undefined;
                newSite.goods = undefined;
                newSite.locationInfo = undefined;
                newSite.projectId = 0;
                newSite.project = undefined;
                newSite.siteType = SiteType.Warehouse;
                newSite.data = undefined;

                state.site = newSite;
            })
            .addCase(createNewEmptyConstructionSite, (state: SiteReducerState, action: any) => {
                const newSite: ISiteDto = new SiteDto();

                newSite.id = 0;
                newSite.name = undefined;
                newSite.goods = undefined;
                newSite.locationInfo = undefined;
                newSite.projectId = 0;
                newSite.project = undefined;
                newSite.siteType = SiteType.ConstructionSite;
                newSite.data = undefined;

                state.site = newSite;
            })
            .addCase(updateSiteName, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'There needs to be site if it is being updated');

                state.site! = { ...state.site, name: action.payload };
            })
            .addCase(updateSiteLocationInfo, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'There needs to be site if it is being updated');

                state.site! = { ...state.site, locationInfo: action.payload };
            })
            .addCase(updateSiteSiteType, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'There needs to be site if it is being updated');

                state.site! = { ...state.site, siteType: action.payload };
            })
            .addCase(saveSite.pending, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveSite.fulfilled, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'Error saving site');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(saveSite.rejected, (state: SiteReducerState, action: any) => {
                myAssert(() => state.site !== null, 'Error saving site');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
